/**
 * Identifiers for posture orientation / compensation classification strategies.
 */
export const POSTURE_ORIENTATION_STRATEGY_IDS = [
  'hipDisplacementHorizontalVisualPatternRecognition',
  'hipDisplacementHorizontalVetoExclusion',
  'hipDisplacementHorizontal',
  'clinicalScoreRules',
  'indicatorPointScoreVprOverride',
  'indicatorPointScoreVpr',
  'simplifiedAutoPilot',
  'adaptiveThresholdVprOverride',
  'adaptiveThresholdVprOverrideV2',
  'adaptiveThresholdVprOverrideV3',
  'adaptiveThresholdVprOverrideV4',
  'adaptiveThresholdVprOverrideV5',
  'adaptiveThresholdVprOverrideV6',
] as const;

export type PostureOrientationStrategyId = (typeof POSTURE_ORIENTATION_STRATEGY_IDS)[number];

export const DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID: PostureOrientationStrategyId =
  'adaptiveThresholdVprOverrideV5';

export const POSTURE_ORIENTATION_STRATEGY_OPTIONS: {
  id: PostureOrientationStrategyId;
  /** Internal name kept for logs and tooltips. Not shown as the select value. */
  label: string;
  /** Practitioner-facing select text. Does not include the strategy id. */
  shortLabel: string;
}[] = [
  {
    id: 'indicatorPointScoreVprOverride',
    shortLabel: 'Indicator points with override',
    label: 'Indicator Point Score, Visual Pattern Recognition Strategy, Override (Conor Feedback)',
  },
  {
    id: 'indicatorPointScoreVpr',
    shortLabel: 'Indicator points',
    label: 'Indicator Point score, Visual Pattern Recognition Strategy (Points Only)',
  },
  {
    id: 'simplifiedAutoPilot',
    shortLabel: 'Simplified auto-pilot',
    label: 'Simplified Auto-Pilot Strategy (Extreme Indicators Only)',
  },
  {
    id: 'hipDisplacementHorizontalVisualPatternRecognition',
    shortLabel: 'Hip displacement and pattern',
    label:
      'Horizontal Displacement + Visual Pattern Recognition (head/trunk signal fusion)',
  },
  {
    id: 'hipDisplacementHorizontalVetoExclusion',
    shortLabel: 'Hip displacement with veto',
    label: 'Horizontal Displacement + Veto/Exclusion engine',
  },
  {
    id: 'hipDisplacementHorizontal',
    shortLabel: 'Hip displacement',
    label: 'Horizontal Displacement of the hip relative to the ankle, asis/psis optional',
  },
  {
    id: 'clinicalScoreRules',
    shortLabel: 'Clinical score rules',
    label: 'Clinical score rules (legacy default)',
  },
  {
    id: 'adaptiveThresholdVprOverrideV2',
    shortLabel: 'Adaptive threshold v2',
    label: '20260711 Adaptive Threshold VPR Override v2 (knee-flex tibial guard + Right-view ASIS/PSIS)',
  },
  {
    id: 'adaptiveThresholdVprOverride',
    shortLabel: 'Adaptive threshold',
    label: '20260711 Adaptive Threshold VPR Override (tibial ant knee-flex guard)',
  },
  {
    id: 'adaptiveThresholdVprOverrideV3',
    shortLabel: 'Adaptive threshold v3',
    label: '20260713 Adaptive Threshold VPR Override v3 (PLC and locked-knee tuning)',
  },
  {
    id: 'adaptiveThresholdVprOverrideV5',
    shortLabel: 'Adaptive threshold v5',
    label:
      '20260911 Adaptive Threshold VPR Override v5 (bilateral sagittal consensus + YAML-complete guards)',
  },
  {
    id: 'adaptiveThresholdVprOverrideV6',
    shortLabel: 'Adaptive threshold v6',
    label:
      '20260912 Adaptive Threshold VPR Override v6 (mild hip-forward hyperext + posterior-knee swayback)',
  },
  {
    id: 'adaptiveThresholdVprOverrideV4',
    shortLabel: 'Adaptive threshold v4',
    label: '20260715 Adaptive Threshold VPR Override v4 (PLC refined and estimated pelvic-tilt guard)',
  },
];

export function normalizePostureOrientationStrategyId(
  value: string | undefined | null
): PostureOrientationStrategyId {
  if (value === 'hipDisplacementHorizontalVisualPatternRecognition') {
    return 'hipDisplacementHorizontalVisualPatternRecognition';
  }
  if (value === 'hipDisplacementHorizontalVetoExclusion') {
    return 'hipDisplacementHorizontalVetoExclusion';
  }
  if (value === 'hipDisplacementHorizontal') {
    return 'hipDisplacementHorizontal';
  }
  if (value === 'clinicalScoreRules') {
    return 'clinicalScoreRules';
  }
  if (value === 'indicatorPointScoreVprOverride') {
    return 'indicatorPointScoreVprOverride';
  }
  if (value === 'indicatorPointScoreVpr') {
    return 'indicatorPointScoreVpr';
  }
  if (value === 'simplifiedAutoPilot') {
    return 'simplifiedAutoPilot';
  }
  if (value === 'adaptiveThresholdVprOverride') {
    return 'adaptiveThresholdVprOverride';
  }
  if (value === 'adaptiveThresholdVprOverrideV2') {
    return 'adaptiveThresholdVprOverrideV2';
  }
  if (value === 'adaptiveThresholdVprOverrideV3') {
    return 'adaptiveThresholdVprOverrideV3';
  }
  if (value === 'adaptiveThresholdVprOverrideV4') {
    return 'adaptiveThresholdVprOverrideV4';
  }
  if (value === 'adaptiveThresholdVprOverrideV5') {
    return 'adaptiveThresholdVprOverrideV5';
  }
  if (value === 'adaptiveThresholdVprOverrideV6') {
    return 'adaptiveThresholdVprOverrideV6';
  }
  return DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID;
}

/**
 * Strategy used at runtime. Non-experimental users always get the default;
 * experimental users may select any registered strategy via settings.
 */
/** Strategies that accept practitioner-toggled visual findings (pooch belly, rib flare, etc.). */
export const POSTURE_STRATEGIES_WITH_PRACTITIONER_OBSERVATIONS: readonly PostureOrientationStrategyId[] = [
  'hipDisplacementHorizontalVisualPatternRecognition',
  'indicatorPointScoreVprOverride',
  'indicatorPointScoreVpr',
  'adaptiveThresholdVprOverride',
  'adaptiveThresholdVprOverrideV2',
  'adaptiveThresholdVprOverrideV3',
  'adaptiveThresholdVprOverrideV4',
  'adaptiveThresholdVprOverrideV5',
  'adaptiveThresholdVprOverrideV6',
];

export function postureStrategySupportsPractitionerObservations(
  strategyId: PostureOrientationStrategyId
): boolean {
  return POSTURE_STRATEGIES_WITH_PRACTITIONER_OBSERVATIONS.includes(strategyId);
}

export function resolvePostureOrientationStrategyId(
  storedId: string | undefined | null,
  experimentalFeaturesEnabled: boolean,
  isPractitioner: boolean = false
): PostureOrientationStrategyId {
  if (isPractitioner) {
    if (!experimentalFeaturesEnabled) {
      return 'adaptiveThresholdVprOverrideV5';
    }
    if (storedId && POSTURE_ORIENTATION_STRATEGY_IDS.includes(storedId as any)) {
      return storedId as PostureOrientationStrategyId;
    }
    return 'adaptiveThresholdVprOverrideV5';
  } else {
    if (!experimentalFeaturesEnabled) {
      return DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID;
    }
    return normalizePostureOrientationStrategyId(storedId);
  }
}

